import {
  Button,
  Stack,
  List,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  ListSubheader,
} from '@mui/material'
import type { JSX, SelectChangeEvent } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Transfer } from '@/api/definition'
import { Status } from '@/api/definition'
import { fetchEndpoints, pushOut } from '@/api/external_actions'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import { SelectedDevicesContext } from '@/pages/inService/takeOutofService/reducers'
import DeviceListItem from '@/pages/intake/component/DeviceListItem'
import types from '@/pages/Device/DeviceType'
import type { Endpoints } from '@/pages/utils/endpointSchema'
import EndpointsSchema from '@/pages/utils/endpointSchema'
import useResponseValidator from '@/pages/utils/responseValidator'

interface TakeOutOfServiceStepProps {
  setActiveStep: (step: number) => void
}

const TakeOutOfServiceStep = ({
  setActiveStep,
}: TakeOutOfServiceStepProps): JSX.Element => {
  const { t } = useTranslation(['di', 'app', 'common'])

  // State management
  const [confirmed, setConfirmed] = useState(false)
  const [endpoints, setEndpoints] = useState<Endpoints | null>(null)
  const [endpoint, setEndpoint] = useState('')
  const [requests, setRequests] = useState<Map<string, Transfer>>(new Map())
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [statusBeingUpdated, setStatusBeingUpdated] = useState(false)
  const [touched, setTouched] = useState(false)

  // Validation and context
  const { validate, showTransferError } = useResponseValidator() // Add showTransferError
  const { state: selectedDevicesState } = useContext(SelectedDevicesContext)

  const selectedDevices = useMemo(
    () => Object.values(selectedDevicesState.selectedDevices),
    [selectedDevicesState],
  )

  // Load endpoints for taking devices out of service
  useEffect(() => {
    let isCancelled = false

    const loadEndpoints = async () => {
      if (endpoints) return

      try {
        const response = await fetchEndpoints()
        if (isCancelled) return

        const validatedEndpoints = await validate<Endpoints>(
          EndpointsSchema,
          response,
        )
        setEndpoints(validatedEndpoints)
      } catch (error) {
        console.error('Failed to load endpoints:', error)
      }
    }

    loadEndpoints()

    return () => {
      isCancelled = true
    }
  }, [endpoints, validate])

  // Reset state when selected devices change
  useEffect(() => {
    setConfirmed(false)
    setRequests(new Map())
  }, [selectedDevices])

  // Handle endpoint selection
  const handleEndpointChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value
      if (typeof value === 'string') {
        setEndpoint(value)
      }
    },
    [],
  )

  const handleEndpointClose = useCallback(() => {
    setTouched(true)
  }, [])

  // Update service status for a single device (take OUT of service)
  const updateServiceStatus = useCallback(
    async (uid: string) => {
      const oldRequest = requests.get(uid)
      if (oldRequest && oldRequest.status !== Status.FAILURE) return

      // Set in progress status
      setRequests((prevRequests) => {
        const newRequests = new Map(prevRequests)
        newRequests.set(uid, { status: Status.IN_PROGRESS })
        return newRequests
      })

      try {
        const result = await pushOut(uid, endpoint)

        // Update the request status
        setRequests((prevRequests) => {
          const newRequests = new Map(prevRequests)
          newRequests.set(uid, result)
          return newRequests
        })

        // Use showTransferError for consistent error handling like other pages
        if (result.status === Status.FAILURE) {
          showTransferError(result)
        }
      } catch (error) {
        // This shouldn't happen since transfer function returns Transfer objects
        console.error(`Unexpected error for device ${uid}:`, error)

        const fallbackError: Transfer = {
          status: Status.FAILURE,
          reason: 'app:device.transfer.error',
          concerns: [
            {
              message: error instanceof Error ? error.message : 'Unknown error',
            },
          ],
        }
        setRequests((prevRequests) => {
          const newRequests = new Map(prevRequests)
          newRequests.set(uid, fallbackError)
          return newRequests
        })
      }
    },
    [endpoint, requests, showTransferError],
  )

  // Update service status for all selected devices
  const updateServiceStatuses = useCallback(async () => {
    if (statusBeingUpdated) return

    setStatusBeingUpdated(true)
    console.log(
      'Starting batch update to take',
      selectedDevices.length,
      'devices out of service',
    )

    try {
      // Use Promise.allSettled to handle all requests, even if some fail
      await Promise.allSettled(
        selectedDevices.map(async (device) => updateServiceStatus(device.uid)),
      )
    } finally {
      setStatusBeingUpdated(false)
    }
  }, [selectedDevices, statusBeingUpdated, updateServiceStatus])

  // Navigation handlers
  const handleBack = useCallback(() => {
    console.log(
      'Navigating back with selections:',
      Object.keys(selectedDevicesState.selectedDevices),
    )
    setActiveStep(0)
  }, [selectedDevicesState.selectedDevices, setActiveStep])

  // Confirm dialog handlers
  const handleConfirm = useCallback(async () => {
    setIsDialogOpen(false)
    setConfirmed(true)
    await updateServiceStatuses()
  }, [updateServiceStatuses])

  const handleActionClick = useCallback(() => {
    if (confirmed) {
      updateServiceStatuses()
    } else {
      setIsDialogOpen(true)
    }
  }, [confirmed, updateServiceStatuses])

  // Computed values
  const hasSelectedDevices = selectedDevices.length > 0
  const isEndpointValid = endpoint !== ''
  const canProceed =
    hasSelectedDevices && isEndpointValid && !statusBeingUpdated

  return (
    <Box
      sx={{ width: '100%', backgroundColor: '#f5f5f5', borderRadius: 1, p: 2 }}
    >
      {hasSelectedDevices ? (
        <List
          sx={{
            width: '100%',
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
            marginBottom: 3,
          }}
        >
          {selectedDevices.map(({ type, serial, label, uid }) => (
            <DeviceListItem
              key={uid}
              serialNr={serial}
              ownerLabel={label}
              deviceTypeName={types[type] ?? type}
              request={requests.get(uid)}
              onRetryClick={async () => updateServiceStatus(uid)}
            />
          ))}
        </List>
      ) : (
        <Typography variant="body1" sx={{ p: 2, mb: 2 }}>
          {t('app:device.noDevicesSelected')}
        </Typography>
      )}

      {/* Endpoint Selection - Required for taking devices out of service */}
      {endpoints && (
        <FormControl
          fullWidth
          required
          error={touched && !endpoint}
          sx={{ mb: 2 }}
        >
          <InputLabel id="endpoint-selector-label">
            {t('di:device.inService.selectEndpoint')}
          </InputLabel>
          <Select
            labelId="endpoint-selector-label"
            id="endpoint-selector"
            value={endpoint}
            label={t('di:device.inService.selectEndpoint')}
            onClose={handleEndpointClose}
            onChange={handleEndpointChange}
            sx={{ mb: 2 }}
            variant="standard"
          >
            {/* Production Endpoints */}
            {endpoints.production?.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}

            {/* Development Endpoints */}
            {endpoints.development?.length > 0 && (
              <ListSubheader>Development Environments</ListSubheader>
            )}
            {endpoints.development?.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>

          {touched && !endpoint && (
            <FormHelperText>
              {t('di:device.inService.missingEndpoint')}
            </FormHelperText>
          )}
        </FormControl>
      )}

      {/* Loading Indicator */}
      {statusBeingUpdated && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Action Buttons */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 3, justifyContent: 'flex-start', alignItems: 'center' }}
      >
        <Button
          data-testid="back-button"
          variant="contained"
          onClick={handleBack}
          sx={{
            marginTop: 2,
            marginRight: 2,
            color: '#fff',
          }}
        >
          {t('common:general.back')}
        </Button>

        <Button
          variant="contained"
          color="takeoutofservice"
          disabled={!canProceed}
          onClick={handleActionClick}
        >
          {t('app:device.inService.takeOutOfService')}
        </Button>
      </Stack>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        onConfirm={handleConfirm}
        open={isDialogOpen}
        setOpen={setIsDialogOpen}
      >
        {t('app:device.inService.willTakeAllOutOfService')}
      </ConfirmDialog>
    </Box>
  )
}

export default TakeOutOfServiceStep
